/**
 * Общие части сопоставлений «должность → категория ФОТ» и «площадка → центр
 * затрат»: комментарий, плашка «изменил кто-то другой» с «Перечитать» и
 * журнал правок «было → стало».
 */
import { forwardRef, useId } from 'react';
import styled from 'styled-components';
import {
  apiLoadErrorText,
  useGetEconChronumPositionHistoryQuery,
  useGetEconChronumSiteHistoryQuery,
  type ChronumPayrollCategory,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { TextArea } from '@shared/ui/Input';
import { SkeletonLine } from '@shared/ui/Skeleton';
import { fleetLeading, fleetType } from '@entities/fleet';
import { formatDateTime, revisionChanges, typo } from '@entities/chronum';
import { FieldHint, Notice, TextButton } from '@widgets/FleetRegistry/ui';
import { NARROW } from './ui';

/** Самый длинный комментарий — как на сервере. */
export const MAX_COMMENT = 2000;

/** Ошибка записи сопоставления: `stale` — 409, запись изменил кто-то другой. */
export interface SaveError {
  message: string;
  stale: boolean;
}

/** Комментарий и журнал рядом: на широком — две колонки, на узком — друг под другом. */
export const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: ${({ theme }) => `${theme.spacing(4)} ${theme.spacing(8)}`};
  align-items: start;

  @container chronum (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const DetailsTitle = styled.h4`
  margin: ${({ theme }) => `0 0 ${theme.spacing(2)}`};
  font-size: ${fleetType.micro};
  font-weight: 600;
  letter-spacing: 0.06em;
  line-height: ${fleetLeading.tight};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Muted = styled.p`
  margin: 0;
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-wrap: pretty;
`;

const CommentBox = styled(TextArea)`
  min-height: 72px;
  resize: vertical;
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};
`;

const FieldWrap = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(1.5)};

  & > label {
    font-size: ${fleetType.body};
    font-weight: 600;
    line-height: ${fleetLeading.tight};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  & .count {
    font-variant-numeric: tabular-nums;
  }
`;

interface CommentFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

/** Поле комментария: для коллег — почему сопоставлено именно так. */
export const CommentField = forwardRef<HTMLTextAreaElement, CommentFieldProps>(
  function CommentField({ label, value, onChange, disabled }, ref) {
    const id = useId();
    const hintId = useId();
    const left = MAX_COMMENT - value.length;
    return (
      <FieldWrap>
        <label htmlFor={id}>{label}</label>
        <CommentBox
          ref={ref}
          id={id}
          rows={3}
          value={value}
          maxLength={MAX_COMMENT}
          disabled={disabled}
          aria-describedby={hintId}
          placeholder="Например: «мастера участков считаем ИТР»"
          onChange={(e) => onChange(e.target.value)}
        />
        <FieldHint id={hintId}>
          {typo('Для коллег: почему сопоставлено так. Видно в списке и в журнале правок.')}
          {left < 200 && <span className="count">{` Осталось ${left} знаков.`}</span>}
        </FieldHint>
      </FieldWrap>
    );
  },
);

/** Комментарий без права правки. */
export function CommentView({ value }: { value: string | null }) {
  return value ? <Muted as="blockquote">{value}</Muted> : <Muted>Комментария нет.</Muted>;
}

const Stale = styled(Notice)`
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

/**
 * 409: пока человек правил, запись изменил кто-то другой. Его правка не
 * перетирается молча — «Перечитать» показывает актуальное, черновик остаётся.
 */
export function SaveErrorNotice({
  error,
  onReread,
  onRetry,
  busy,
}: {
  error: SaveError;
  onReread: () => void;
  onRetry?: () => void;
  busy?: boolean;
}) {
  if (!error.stale) {
    return (
      <Stale $tone="danger" role="alert">
        <span className="text">{error.message}</span>
        {onRetry && (
          <TextButton type="button" onClick={onRetry} disabled={busy}>
            Повторить
          </TextButton>
        )}
      </Stale>
    );
  }
  return (
    <Stale $tone="warning" role="alert">
      <span className="text">
        <strong>{error.message}</strong>{' '}
        {typo(
          'Перечитайте: появится то, что сохранил коллега. Ваш текст останется в поле — сохраните ещё раз, если он нужнее.',
        )}
      </span>
      <Button type="button" $variant="secondary" $size="sm" disabled={busy} onClick={onReread}>
        {busy ? 'Перечитываю…' : 'Перечитать'}
      </Button>
    </Stale>
  );
}

const Timeline = styled.ol`
  position: relative;
  margin: 0;
  padding: 0;
  list-style: none;

  &::before {
    content: '';
    position: absolute;
    top: 8px;
    bottom: 8px;
    left: 4px;
    width: 1px;
    background: ${({ theme }) => theme.colors.border};
  }
`;

const Entry = styled.li`
  position: relative;
  padding: ${({ theme }) => `0 0 ${theme.spacing(4)} ${theme.spacing(5)}`};

  &::before {
    content: '';
    position: absolute;
    top: 6px;
    left: 0;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.backgroundElevated};
    box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.primary};
  }

  &:last-child {
    padding-bottom: 0;
  }

  & > .head {
    display: flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(2)}`};
    margin-bottom: ${({ theme }) => theme.spacing(1)};
    font-size: ${fleetType.small};
    line-height: ${fleetLeading.tight};
    color: ${({ theme }) => theme.colors.text.secondary};
    font-variant-numeric: tabular-nums;
  }

  & > .head > .who {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }

  & ul {
    margin: 0;
    padding: 0;
    list-style: none;
    font-size: ${fleetType.body};
    line-height: ${fleetLeading.normal};
  }

  & li + li {
    margin-top: 2px;
  }

  & .field {
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  & del,
  & ins {
    text-decoration: none;
    overflow-wrap: anywhere;
  }

  & del {
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  & del[data-empty='false'] {
    text-decoration: line-through;
    text-decoration-color: ${({ theme }) => theme.colors.text.muted};
  }

  & ins {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }

  & .arrow {
    margin: ${({ theme }) => `0 ${theme.spacing(1)}`};
    color: ${({ theme }) => theme.colors.text.muted};
  }

  @container chronum (max-width: ${NARROW}) {
    padding-left: ${({ theme }) => theme.spacing(4)};
  }
`;

interface JournalProps {
  entity: 'position' | 'site';
  entityKey: string;
  categories: readonly ChronumPayrollCategory[];
}

/** Журнал правок сопоставления: кто, когда, что было и что стало. Свежие сверху. */
export function MappingJournal({ entity, entityKey, categories }: JournalProps) {
  const position = useGetEconChronumPositionHistoryQuery(entityKey, {
    skip: entity !== 'position',
  });
  const site = useGetEconChronumSiteHistoryQuery(entityKey, { skip: entity !== 'site' });
  const q = entity === 'position' ? position : site;

  if (q.isError) {
    return (
      <Notice $tone="danger" role="alert">
        <span className="text">
          {apiLoadErrorText(
            q.error,
            'Журнал правок не загрузился.',
            'Нет доступа к журналу правок.',
          )}
        </span>
        <TextButton type="button" onClick={() => void q.refetch()}>
          Повторить
        </TextButton>
      </Notice>
    );
  }
  if (!q.data) {
    return (
      <div aria-busy="true" aria-label="Загрузка журнала правок">
        <SkeletonLine $width="40%" $height={10} />
        <SkeletonLine $width="85%" $height={14} />
        <SkeletonLine $width="70%" $height={14} />
      </div>
    );
  }
  if (q.data.items.length === 0) return <Muted>Правок пока не было.</Muted>;

  return (
    <Timeline aria-label="Журнал правок" aria-busy={q.isFetching}>
      {q.data.items.map((r) => (
        <Entry key={r.id}>
          <div className="head">
            <time dateTime={r.changed_at}>{formatDateTime(r.changed_at)}</time>
            <span className="who">{r.changed_by_name?.trim() || 'сотрудник удалён'}</span>
          </div>
          <ul>
            {revisionChanges(r, categories).map((c) => (
              <li key={c.field}>
                <span className="field">{c.label}: </span>
                <del data-empty={c.before === null ? 'true' : 'false'}>{c.before ?? '—'}</del>
                <span className="arrow" aria-label="стало">
                  →
                </span>
                <ins>{c.after ?? '—'}</ins>
              </li>
            ))}
          </ul>
        </Entry>
      ))}
    </Timeline>
  );
}
